import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthInterceptor } from './auth.interceptor';
import { ClientLoggingService } from './client-logging.service';
import { EnvironmentService } from './environment.service';
import { FileMetadataService } from './file-metadata.service';
import { highlightQueryParams } from './highlight-regions';
import { ArchitecturalPlanPage } from './models/architectural-plan-page.model';
import { extractFileIdFromPage } from './page-path.util';
import { PdfCacheService } from './pdf-cache.service';
import { PlanReviewerService } from './plan-reviewer.service';
import {
  ProjectSearchFile,
  ProjectSearchHit,
  ProjectSearchIndexState,
  ProjectSearchMatch,
  ProjectSearchPage,
  ProjectSearchWordSource,
  indexNotice,
  previewCommands,
  toPermilleBox,
} from './project-search-results';
import { PassageSearchServiceClient, RegionNavigationServiceClient } from '../../generated.commonjs/ApiServiceClientPb';
import {
  PassageIndexState,
  PassageMatch,
  PlanPassageHit,
  PlanTextSearchHit,
  SearchPlanPassagesRequest,
  SearchPlanTextRequest,
} from '../../generated.commonjs/api_pb';

/** What one search found, and what the panel should say about it. */
export interface ProjectSearchResult {
  /** `passages` for passage search; `exact` when the project has no passage index. */
  kind: 'passages' | 'exact';
  query: string;
  hits: ProjectSearchHit[];
  /** A sentence about the index or the fallback, or `null` when there is nothing to say. */
  notice: string | null;
  /**
   * `warning` when results are missing because of the index: there is none, so only exact words
   * are found, or it covers only some pages. `info` when results are complete but may be stale.
   */
  noticeTone: 'info' | 'warning';
  /** What was searched, from the server: the index summary, or the exact search's coverage. */
  summary: string;
}

/**
 * The project's pages and files as the panel names them: sheet titles, transcripts and file
 * names. All of it comes from the plan the project page has already loaded, plus one file listing.
 */
export interface ProjectSearchCatalog {
  /** Every page, in plan order. */
  pages: ProjectSearchPage[];
  /** Every file with pages, in project order. */
  files: ProjectSearchFile[];
  page(fileId: string, pageNumber: number): ProjectSearchPage | undefined;
  /** The file's name, or "File <id>" when the listing did not name it. */
  fileName(fileId: string): string;
}

/** A Preview route with the highlight for one hit. */
export interface ProjectSearchLink {
  commands: string[];
  queryParams: Record<string, string | null>;
}

/** Most passages asked for. Enough for a panel of results grouped by page. */
export const MAX_PASSAGE_RESULTS = 30;

/** Most exact matches asked for when a project has no passage index. */
export const MAX_EXACT_HITS = 50;

const MATCHES: Record<number, ProjectSearchMatch> = {
  [PassageMatch.PASSAGE_MATCH_KEYWORD]: 'words',
  [PassageMatch.PASSAGE_MATCH_MEANING]: 'meaning',
  [PassageMatch.PASSAGE_MATCH_KEYWORD_AND_MEANING]: 'words-and-meaning',
};

const INDEX_STATES: Record<number, ProjectSearchIndexState> = {
  [PassageIndexState.PASSAGE_INDEX_STATE_CURRENT]: 'current',
  [PassageIndexState.PASSAGE_INDEX_STATE_PARTIAL]: 'partial',
  [PassageIndexState.PASSAGE_INDEX_STATE_OUTDATED]: 'outdated',
};

/** Said with exact results, so a person knows why plain-words questions find little. */
export const NO_INDEX_NOTICE =
  'This project has no search index yet, so these are exact matches for the words as typed.';

/**
 * Most files asked for in one listing. Projects have a few dozen at most; a project with more
 * shows "File <id>" for the rest.
 */
const MAX_LISTED_FILES = 200;

const WORD_SOURCES: Record<string, ProjectSearchWordSource> = { TEXT_LAYER: 'text-layer', OCR: 'ocr' };

/**
 * Searches a whole project from the top app bar (Issue #2750): passage search first, and exact
 * search when the project has no passage index. A project with a plan entity index answers with
 * plan objects (Issue #3212), each with its kind, name and outline. Errors are thrown to the
 * caller, which shows them.
 */
@Injectable({ providedIn: 'root' })
export class ProjectSearchService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);
  private planReviewer = inject(PlanReviewerService);
  private fileMetadata = inject(FileMetadataService);
  private pdfCache = inject(PdfCacheService);
  /** File names by project; a listing that failed is dropped so the next search asks again. */
  private fileNames = new Map<string, Promise<Map<string, string>>>();
  private passageClient: PassageSearchServiceClient;
  private textClient: RegionNavigationServiceClient;

  constructor() {
    const host = `https://${this.environmentService.getApiServer()}:443`;
    const options = { unaryInterceptors: [this.authInterceptor] };
    this.passageClient = new PassageSearchServiceClient(host, null, options);
    this.textClient = new RegionNavigationServiceClient(host, null, options);
  }

  /**
   * Searches every page of `projectId` for `query`. `onExactSearch` is called when the project
   * turns out to have no passage index, before the slower exact search starts, so the panel can
   * say why the wait is long.
   */
  async search(projectId: string, query: string, onExactSearch?: () => void): Promise<ProjectSearchResult> {
    const request = new SearchPlanPassagesRequest();
    request.setArchitecturalPlanId(projectId);
    request.setQuery(query);
    request.setMaxResults(MAX_PASSAGE_RESULTS);
    const response = await this.passageClient.searchPlanPassages(request, null);
    if (!response.getIndexed()) {
      onExactSearch?.();
      return this.searchExact(projectId, query);
    }
    const state = INDEX_STATES[response.getIndexState()] ?? 'unknown';
    return {
      kind: 'passages',
      query,
      hits: response.getHitsList().map(passageHit),
      notice: indexNotice(state, response.getPagesIndexed(), response.getPagesTotal()),
      noticeTone: state === 'partial' ? 'warning' : 'info',
      summary: response.getIndexSummary(),
    };
  }

  private async searchExact(projectId: string, query: string): Promise<ProjectSearchResult> {
    const request = new SearchPlanTextRequest();
    request.setArchitecturalPlanId(projectId);
    request.setQuery(query);
    request.setMaxHits(MAX_EXACT_HITS);
    const response = await this.textClient.searchPlanText(request, null);
    return {
      kind: 'exact',
      query,
      hits: response.getHitsList().map(exactHit),
      notice: NO_INDEX_NOTICE,
      noticeTone: 'warning',
      // The coverage summary names every file and is a paragraph long on a large project; the
      // panel needs one line.
      summary:
        response.getPagesListed() > 0
          ? `${response.getPagesSearched()} of ${response.getPagesListed()} pages searched`
          : response.getCoverageSummary(),
    };
  }

  /**
   * Builds a Preview link without fetching the PDF. The search API already returns a normalized
   * display-page box; Preview converts it to points after navigation when the parsed page is ready.
   */
  previewLink(projectId: string, hit: ProjectSearchHit, label?: string): ProjectSearchLink {
    const commands = previewCommands(projectId, hit.fileId, hit.pageNumber);
    const box = toPermilleBox(hit.box);
    return { commands, queryParams: box ? highlightQueryParams([{ box, label }], null, 'permille') : {} };
  }

  /** Warms exactly one result page through the parsed-PDF cache; callers bound when it is used. */
  async preload(projectId: string, hit: Pick<ProjectSearchHit, 'fileId' | 'pageNumber'>): Promise<void> {
    const page = await this.findPage(projectId, hit.fileId, hit.pageNumber);
    if (page) await this.pdfCache.getParsedPage(page);
  }

  /**
   * The project's pages and files for naming results. The plan is the one the project page loaded
   * (the plan service caches it); the file names come from one listing, kept for the session.
   */
  async catalog(projectId: string): Promise<ProjectSearchCatalog> {
    const [plan, names] = await Promise.all([
      this.planReviewer.getArchitecturalPlan(projectId),
      this.namesOf(projectId),
    ]);
    const planPages: ArchitecturalPlanPage[] = plan?.getPagesList?.() ?? [];
    const pages = planPages.map(page => ({
      fileId: extractFileIdFromPage(page) ?? '1',
      pageNumber: page.getPageNumber(),
      sheetId: page.getPageId?.() ?? '',
      title: (page.getTitle?.() ?? '').trim(),
      transcript: page.getPageMarkdown?.() ?? '',
    }));
    const byKey = new Map(pages.map(page => [`${page.fileId}/${page.pageNumber}`, page]));
    const files = new Map<string, ProjectSearchFile>();
    for (const page of pages) {
      const file = files.get(page.fileId);
      if (file) {
        file.pages++;
        file.firstPage = Math.min(file.firstPage, page.pageNumber);
      } else {
        const name = names.get(page.fileId) ?? `File ${page.fileId}`;
        files.set(page.fileId, { fileId: page.fileId, name, firstPage: page.pageNumber, pages: 1 });
      }
    }
    return {
      pages,
      files: [...files.values()],
      page: (fileId, pageNumber) => byKey.get(`${fileId || '1'}/${pageNumber}`),
      fileName: fileId => files.get(fileId || '1')?.name ?? `File ${fileId || '1'}`,
    };
  }

  /** The project's file names by file ID; empty when the listing fails, so results still show. */
  private namesOf(projectId: string): Promise<Map<string, string>> {
    let names = this.fileNames.get(projectId);
    if (!names) {
      names = firstValueFrom(this.fileMetadata.listFileMetadata(projectId, MAX_LISTED_FILES))
        .then(response => new Map(response.getFilesList().map(file => [file.getFileId(), file.getFileName()])))
        .catch(error => {
          ClientLoggingService.log('WARN', '[ProjectSearchService] Could not list the files:', error);
          this.fileNames.delete(projectId);
          return new Map<string, string>();
        });
      this.fileNames.set(projectId, names);
    }
    return names;
  }

  private async findPage(projectId: string, fileId: string, pageNumber: number): Promise<ArchitecturalPlanPage | null> {
    const plan = await this.planReviewer.getArchitecturalPlan(projectId);
    const pages: ArchitecturalPlanPage[] = plan?.getPagesList?.() ?? [];
    return (
      pages.find(
        page => page.getPageNumber() === pageNumber && (extractFileIdFromPage(page) ?? '1') === (fileId || '1')
      ) ?? null
    );
  }
}

/** A passage-search hit in the panel's terms; a plan object (Issue #3212) keeps its kind and name. */
function passageHit(hit: PlanPassageHit): ProjectSearchHit {
  const entity = hit.hasEntity() ? hit.getEntity() : undefined;
  return {
    fileId: hit.getFileId(),
    pageNumber: hit.getPageNumber(),
    sheetId: hit.getSheetId(),
    text: hit.getText(),
    words: '',
    source: entity ? 'object' : 'sheet',
    box: hit.getBoundingBoxList(),
    match: MATCHES[hit.getMatchedBy()] ?? 'words',
    keywordRank: hit.getKeywordRank(),
    meaningRank: hit.getMeaningRank(),
    ...(entity
      ? {
          entity: {
            id: entity.getId(),
            kind: entity.getKind(),
            title: entity.getTitle(),
            path: entity.getPath(),
            parentTitle: entity.getParentTitle(),
          },
        }
      : {}),
  };
}

/** An exact-search hit in the panel's terms: its context is the text shown. */
function exactHit(hit: PlanTextSearchHit): ProjectSearchHit {
  return {
    fileId: hit.getFileId(),
    pageNumber: hit.getPageNumber(),
    sheetId: hit.getSheetId(),
    text: hit.getContext() || hit.getWords(),
    words: hit.getWords(),
    source: WORD_SOURCES[hit.getSource()] ?? 'sheet',
    box: hit.getBoundingBoxList(),
    match: 'exact',
  };
}
